import React, { memo } from 'react';
import styled, { css } from 'styled-components';
import { match } from 'ts-pattern';
import {
  ContactStage,
  ContactStageSlug,
  ContactStatus,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { ContactStageIcon } from 'src/components/domain/contacts/contact-stage-icon';
import { Icon } from 'src/components/general/icon';
import { FlexColumn } from 'src/components/layout/flex-column';
import { showContactStatusModal } from './contact-status-modal';

const StyledContactStageIcon = styled(ContactStageIcon)`
  & {
    height: 24px;
    min-width: 24px;
    width: 24px;
  }
`;

const ContactStageName = styled.span`
  color: ${({ theme }) => theme.colors.steel};
  font-family: ${({ theme }) => theme.font.bold};
  font-size: 9px;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  line-height: 11px;
  text-align: left;
  text-transform: uppercase;
`;

const ContactStatusName = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 11px;
  font-stretch: normal;
  font-style: normal;
  font-weight: normal;
  letter-spacing: normal;
  line-height: 14px;
  text-align: left;
`;

const ArrowIcon = styled(Icon)`
  svg {
    height: 10px;
    width: 10px;

    path {
      fill: ${({ theme }) => theme.colors.casper};
    }
  }
`;

const StatusDescriptionContainer = styled(FlexColumn)`
  width: 100%;
`;

const Separator = styled.div`
  color: ${({ theme }) => theme.colors.koala};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  line-height: 15px;
`;

const ContactStatusContainer = styled.div<{
  $variant: 'regular' | 'inline';
  $stageSlug?: ContactStageSlug;
}>`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  border: solid 1px ${({ theme }) => theme.colors.koala};
  cursor: pointer;
  display: flex;
  flex-direction: row;
  height: 36px;
  padding-left: 6px;
  padding-right: 15px;
  width: 100%;

  ${({ $variant }) =>
    $variant === 'regular' &&
    css`
      padding-right: 15px;

      ${StyledContactStageIcon} {
        & {
          border-width: 2px;

          svg {
            height: 12px;
            width: 12px;
          }
        }
      }
    `}

  ${({ $variant }) =>
    $variant === 'inline' &&
    css`
      height: 30px;
      padding: 4px 8px;
      width: max-content;

      ${StyledContactStageIcon} {
        margin-right: 5px;
        & {
          border-width: 2px;
          height: 21px;
          min-width: 21px;
          width: 21px;

          svg {
            height: 11px;
            width: 11px;
          }
        }
      }

      ${StatusDescriptionContainer} {
        align-items: center;
        flex-direction: row;
        gap: 4px;
      }

      ${ContactStageName} {
        font-size: 10px;
        line-height: 13px;
        white-space: nowrap;
      }

      ${ContactStatusName} {
        color: ${({ theme }) => theme.colors.smalt};
        font-family: ${({ theme }) => theme.font.regular};
        font-size: 12px;
        line-height: 15px;
        white-space: nowrap;
      }
    `}

      ${({ $stageSlug }: { $stageSlug?: ContactStageSlug }) => {
    return match($stageSlug)
      .with(
        'LEAD',
        () => css`
          ${ContactStageName} {
            color: ${({ theme }) => theme.colors.gold};
          }
        `,
      )
      .with(
        'CUSTOMER',
        () => css`
          ${ContactStageName} {
            color: ${({ theme }) => theme.colors.mint};
          }
        `,
      )
      .with(
        'OPPORTUNITY',
        () => css`
          ${ContactStageName} {
            color: ${({ theme }) => theme.colors.topaz};
          }
        `,
      )
      .otherwise(() => null);
  }}
`;

type ContactStatusBadgeProps = {
  stage: ContactStage;
  status: ContactStatus;
  onClick: VoidFunction;
  variant?: 'regular' | 'inline';
  matchStageColor?: boolean;
};

export const ContactStatusBadge = memo<ContactStatusBadgeProps>(
  ({ stage, status, onClick, variant = 'regular', matchStageColor = false, ...rest }) => {
    const isInlineVariant = variant === 'inline';
    return (
      <ContactStatusContainer
        data-lgg-id="status-modal-trigger"
        onClick={onClick}
        $variant={variant}
        $stageSlug={matchStageColor ? stage.slug : undefined}
        {...rest}
      >
        <StyledContactStageIcon slug={stage.slug} name={status.name} />
        <StatusDescriptionContainer>
          <ContactStageName data-lgg-id="contact-details-stage">
            {stage.name}
          </ContactStageName>
          {isInlineVariant ? <Separator>|</Separator> : null}
          <ContactStatusName data-lgg-id="contact-details-status">
            {status.name}
          </ContactStatusName>
        </StatusDescriptionContainer>
        {!isInlineVariant ? (
          <ArrowIcon type="arrowRightWide" className="arrow-icon" />
        ) : null}
      </ContactStatusContainer>
    );
  },
);

type ContactStatusElementProps = {
  stage: ContactStage;
  status: ContactStatus;
  contactId: number;
};

export const ContactStatusElement = memo<ContactStatusElementProps>(
  ({ stage, status, contactId }) => {
    return (
      <ContactStatusBadge
        stage={stage}
        status={status}
        onClick={() => showContactStatusModal(contactId)}
      />
    );
  },
);
